import { AppSettingsContext } from './context';
import React, { useMemo, useState, useEffect } from 'react';
import { getCategories, getPillars, getPillarExpertises, getContentGroupIds } from '../../api/appSettings';
import { Author } from '../../api/types/author';
import { PriceTier } from '../../api/types/priceTier';
import { getAllAuthors } from '../../api/author';
import { getAllPriceTiers } from '../../api/priceTier';
import { getAllGoals } from '../../api/contentManagement';
import { Goal } from '../../api/types/contentManagement';
import { ContentGroupType } from '../../api/types/others';

const AppSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [pillarTypes, setPillars] = useState<string[]>([]);
  const [pillarExpertises, setPillarExpertises] = useState<string[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [collectionContentGroups, setCollectionContentGroups] = useState<{ title: string; id: string }[]>([]);

  const fetchCategories = async () => {
    const result = await getCategories();
    setCategories(result);
  };

  const fetchPillars = async () => {
    const result = await getPillars();
    setPillars(result);
  };

  const fetchPillarExpertises = async () => {
    const result = await getPillarExpertises();
    setPillarExpertises(result);
  };

  const fetchAuthors = async () => {
    const result = await getAllAuthors();
    setAuthors(result);
  };

  const fetchPriceTiers = async () => {
    const result = await getAllPriceTiers();
    setPriceTiers(result);
  };

  const fetchGoals = async () => {
    const result = await getAllGoals();
    setGoals(result);
  };

  const fetchContentGroupIds = async (type: ContentGroupType) => {
    const result = await getContentGroupIds(type);
    if (type === 'collection') {
      setCollectionContentGroups(result);
    }
  };

  const refreshContext = () => {
    fetchCategories().then();
    fetchPillars().then();
    fetchPillarExpertises().then();
    fetchAuthors().then();
    fetchPriceTiers().then();
    fetchGoals().then();
    fetchContentGroupIds('collection').then();
  };

  useEffect(() => {
    refreshContext();
  }, []);

  const value = useMemo(
    () => ({
      categories,
      pillarTypes,
      pillarExpertises,
      authors,
      priceTiers,
      goals,
      collectionContentGroups,
      refreshContext,
    }),
    [categories, pillarTypes, pillarExpertises, authors, priceTiers, goals, collectionContentGroups, refreshContext],
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
};

export default AppSettingsProvider;
